import {
  readDeviceField,
  readDeviceLabelSerial,
  SDTEST_FIELD_KEYS,
  type SdTestDevice,
} from "@/lib/sdtestapp/sdTestAppHttpClient";
import {
  getDeviceFamily,
  normalizeConfigurationSkuForFamilyDetection,
  unsupportedSkuMessage,
} from "@integrator-src/manufacturing/configuration-sku-family";
import {
  CONFIGURATION_PCBA_SERIAL_FORMAT_ERROR,
  isConfigurationYearEncodedClampSerial,
  matchesConfigurationPcbaSerialFormat,
} from "@integrator-src/manufacturing/configuration-pcba-serial";
import { validateWirelessIccidMatchesSkuCarrier } from "@integrator-src/manufacturing/sim-iccid-carrier";
import {
  BARE_SDTEST_DEVICE_LABEL_SUFFIX_MESSAGE,
  isBareSdTestDeviceLabelSuffix,
} from "@/lib/sdtestapp/deviceLabelSerial";

export type ConfigurationDeviceType = "patrolmanPlus" | "patrolman" | "reconRelay" | "recon" | "";

export interface ConfigurationFieldDefinition {
  required: boolean;
  label: string;
  validation?: {
    regex: string;
    errorMessage: string;
  };
}

export type ConfigurationFieldConfig = Record<string, ConfigurationFieldDefinition>;

const DEVICE_FIELD_CONFIGS: Record<Exclude<ConfigurationDeviceType, "">, ConfigurationFieldConfig> = {
  patrolmanPlus: {
    iccid: {
      required: true,
      label: "ICCID",
      validation: {
        regex: "^[0-9]{19,20}$",
        errorMessage: "ICCID must be 19-20 digits",
      },
    },
    pcbaSerial: {
      required: true,
      label: "PCBA serial",
    },
    clampSerial: {
      required: true,
      label: "Clamp serial",
      validation: {
        regex: "^(20\\d{8}|[Uu][Ss])$",
        errorMessage: "Clamp serial must be 10 digits starting with 20, or US for domestic clamps",
      },
    },
    deviceLabelSerial: {
      required: true,
      label: "Device label serial",
      validation: {
        regex: "^[A-Za-z0-9]{5,15}$",
        errorMessage: "Device label serial must be 5-15 alphanumeric characters",
      },
    },
  },
  reconRelay: {
    iccid: {
      required: true,
      label: "ICCID",
      validation: {
        regex: "^[0-9]{19,20}$",
        errorMessage: "ICCID must be 19-20 digits",
      },
    },
    pcbaSerial: {
      required: true,
      label: "PCBA serial",
    },
    deviceLabelSerial: {
      required: true,
      label: "Device label serial",
      validation: {
        regex: "^[A-Za-z0-9]{5,15}$",
        errorMessage: "Device label serial must be 5-15 alphanumeric characters",
      },
    },
  },
  patrolman: {
    pcbaSerial: {
      required: true,
      label: "PCBA serial",
    },
    clampSerial: {
      required: true,
      label: "Clamp serial",
      validation: {
        regex: "^(20\\d{8}|[Uu][Ss])$",
        errorMessage: "Clamp serial must be 10 digits starting with 20, or US for domestic clamps",
      },
    },
    deviceLabelSerial: {
      required: true,
      label: "Device label serial",
      validation: {
        regex: "^[A-Za-z0-9]{5,15}$",
        errorMessage: "Device label serial must be 5-15 alphanumeric characters",
      },
    },
  },
  recon: {
    pcbaSerial: {
      required: true,
      label: "PCBA serial",
    },
    clampSerial: {
      required: true,
      label: "Clamp serial",
      validation: {
        regex: "^(20\\d{8}|[Uu][Ss])$",
        errorMessage: "Clamp serial must be 10 digits starting with 20, or US for domestic clamps",
      },
    },
    deviceLabelSerial: {
      required: true,
      label: "Device label serial",
      validation: {
        regex: "^[A-Za-z0-9]{5,15}$",
        errorMessage: "Device label serial must be 5-15 alphanumeric characters",
      },
    },
  },
};

const SCAN_PRIORITY_FIELDS = ["iccid", "pcbaSerial", "clampSerial", "deviceLabelSerial"] as const;

export function getConfigurationDeviceType(sku: string): ConfigurationDeviceType {
  return getDeviceFamily(sku);
}

export function skuRequiresImeiIccid(sku: string): boolean {
  const upper = normalizeConfigurationSkuForFamilyDetection(sku);
  return upper.startsWith("FI-5A-W") || upper.startsWith("DM-1");
}

/** PCBA test is skipped for wireless configure-only families (matches server configuration-sku). */
export function skipsPcbaTestRecordRequirement(sku: string): boolean {
  const upper = normalizeConfigurationSkuForFamilyDetection(sku);
  return upper.startsWith("FI-5A-W") || upper.startsWith("DM-1");
}

export function validateSkuSupportedForConfigure(sku: string): { ok: boolean; message: string } {
  const trimmed = String(sku ?? "").trim();
  if (trimmed === "") {
    return { ok: false, message: "SKU is required." };
  }
  if (!getConfigurationDeviceType(trimmed)) {
    return {
      ok: false,
      message: unsupportedSkuMessage(trimmed),
    };
  }
  return { ok: true, message: "" };
}

export function getConfigurationFieldConfig(deviceType: ConfigurationDeviceType): ConfigurationFieldConfig {
  if (deviceType === "") return {};
  return { ...DEVICE_FIELD_CONFIGS[deviceType] };
}

export function isValidImeiForWireless(imei: string | null | undefined): boolean {
  const value = String(imei ?? "").trim();
  if (value.length === 0) return false;
  if (/^0+$/.test(value)) return false;
  return true;
}

/**
 * SDTestApp `mcu_id.get` (MCU UUID) is available since firmware 6.52.
 * Matches SDTestApp command registry `since_fw='6.52'`.
 */
export const MCU_UUID_SUPPORTED_SINCE_FIRMWARE = "6.52";

/** Leading X.YY from an SDTestApp firmware string (e.g. "6.64" from "6.64.0"). */
export function normalizeConfigureFirmwareVersion(firmware: string | null | undefined): string {
  const trimmed = String(firmware ?? "").trim();
  const match = trimmed.match(/^(\d+\.\d+)/);
  return match?.[1] ?? trimmed;
}

/** Compare two X.YY firmware strings: negative if a < b, 0 if equal, positive if a > b. */
export function compareConfigureFirmwareVersions(a: string, b: string): number {
  const parse = (value: string): { major: number; minor: number } => {
    const [majorText, minorText] = normalizeConfigureFirmwareVersion(value).split(".");
    const major = Number(majorText);
    const minor = Number(minorText);
    return {
      major: Number.isFinite(major) ? major : 0,
      minor: Number.isFinite(minor) ? minor : 0,
    };
  };
  const left = parse(a);
  const right = parse(b);
  if (left.major !== right.major) return left.major - right.major;
  return left.minor - right.minor;
}

/** True when the connected device firmware can report MCU UUID via SDTestApp. */
export function firmwareSupportsMcuUuid(firmware: string | null | undefined): boolean {
  const version = normalizeConfigureFirmwareVersion(firmware);
  if (version === "") return false;
  return compareConfigureFirmwareVersions(version, MCU_UUID_SUPPORTED_SINCE_FIRMWARE) >= 0;
}

export const DEVICE_COMMUNICATION_FAILURE_LEAD =
  "Failed to communicate with device. Disconnect and reconnect connection, refresh device, then try again.";

export const MCU_UUID_UNREAD_DETAIL = "Could not read MCU UUID from the connected device.";

export function deviceCommunicationFailureMessage(detail: string): string {
  const trimmed = detail.trim().replace(/^\(+|\)+$/g, "");
  return `${DEVICE_COMMUNICATION_FAILURE_LEAD} (${trimmed})`;
}

/** Bench program reply that means the cable stopped answering during configure. */
export function deviceCommunicationFailureDetail(rawMessage: string): string | null {
  const trimmed = rawMessage.trim();
  if (trimmed === "" || trimmed.startsWith(DEVICE_COMMUNICATION_FAILURE_LEAD)) return null;
  if (/could not read mcu uuid/i.test(trimmed)) {
    return MCU_UUID_UNREAD_DETAIL;
  }
  const withoutCable = trimmed.replace(/^[A-Za-z0-9]+:\s*/, "");
  if (
    /failed to set configuration profile/i.test(withoutCable) ||
    /no response from device/i.test(withoutCable) ||
    /timeout waiting for reply/i.test(withoutCable)
  ) {
    return withoutCable;
  }
  return null;
}

export function missingMcuUuidWhenSupportedMessage(_firmware: string | null | undefined): string {
  return deviceCommunicationFailureMessage(MCU_UUID_UNREAD_DETAIL);
}

export function validateConfigurationField(
  fieldName: string,
  value: string,
  fieldConfig: ConfigurationFieldConfig,
): { isValid: boolean; error: string | null } {
  const config = fieldConfig[fieldName];
  if (!config) return { isValid: true, error: null };

  const trimmed = String(value ?? "").trim();
  if (config.required && trimmed === "") {
    return { isValid: false, error: `${config.label} is required` };
  }

  if (fieldName === "pcbaSerial" && trimmed !== "") {
    if (!matchesConfigurationPcbaSerialFormat(trimmed)) {
      return { isValid: false, error: CONFIGURATION_PCBA_SERIAL_FORMAT_ERROR };
    }
    return { isValid: true, error: null };
  }

  if (fieldName === "deviceLabelSerial" && trimmed !== "" && isBareSdTestDeviceLabelSuffix(trimmed)) {
    return { isValid: false, error: BARE_SDTEST_DEVICE_LABEL_SUFFIX_MESSAGE };
  }

  if (config.validation?.regex && trimmed !== "") {
    const regex = new RegExp(config.validation.regex);
    if (!regex.test(trimmed)) {
      return { isValid: false, error: config.validation.errorMessage };
    }
  }

  return { isValid: true, error: null };
}

function isDeviceLabelSerialKeyList(keys: readonly string[]): boolean {
  return keys.includes("fciSerialNumber") && keys.includes("deviceLabelSerial");
}

/** Blocks wireless configure when ICCID issuer does not match SKU carrier programming. */
export function validateConfigurationIccidCarrierAlignment(
  iccid: string,
  finishedSku: string,
): { isValid: boolean; error: string | null } {
  if (!skuRequiresImeiIccid(finishedSku)) {
    return { isValid: true, error: null };
  }
  const result = validateWirelessIccidMatchesSkuCarrier(iccid, finishedSku);
  return { isValid: result.ok, error: result.ok ? null : result.message };
}

export function validateConfigurationForm(
  formData: Record<string, string>,
  fieldConfig: ConfigurationFieldConfig,
): { isValid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  for (const fieldName of Object.keys(fieldConfig)) {
    const result = validateConfigurationField(fieldName, formData[fieldName] ?? "", fieldConfig);
    if (!result.isValid && result.error) {
      errors[fieldName] = result.error;
    }
  }
  return { isValid: Object.keys(errors).length === 0, errors };
}

export type ConfigurationScanType = "SUBMIT" | "RESET" | "FIELD" | "UNKNOWN";

/** Scanned or typed into the configuration scanner to trigger submit (Manufacturing-Dashboard configure.js). */
export const CONFIGURATION_SUBMIT_SCAN_VALUE = "SUBMIT_CONFIG";

/** Scanned into the configuration scanner to clear all serial fields. */
export const CONFIGURATION_RESET_SCAN_VALUE = "RESET_CONFIG";

/** Domestic clamp serial stored on serial_fci (also valid when scanned from the US Data Matrix). */
export const CONFIGURATION_US_CLAMP_SCAN_VALUE = "US";

export function identifyConfigurationScan(
  rawValue: string,
  fieldConfig: ConfigurationFieldConfig,
): { type: ConfigurationScanType; fieldName?: string } {
  const normalized = String(rawValue ?? "")
    .trim()
    .replace(/[\r\n]+/g, "");
  if (normalized === "") return { type: "UNKNOWN" };
  if (new RegExp(`^${CONFIGURATION_SUBMIT_SCAN_VALUE}$`, "i").test(normalized)) return { type: "SUBMIT" };
  if (new RegExp(`^${CONFIGURATION_RESET_SCAN_VALUE}$`, "i").test(normalized)) return { type: "RESET" };

  if (/^\d+$/.test(normalized)) {
    if (fieldConfig.iccid && /^[0-9]{19,20}$/.test(normalized)) {
      return { type: "FIELD", fieldName: "iccid" };
    }
    if (fieldConfig.clampSerial && isConfigurationYearEncodedClampSerial(normalized)) {
      return { type: "FIELD", fieldName: "clampSerial" };
    }
    if (fieldConfig.pcbaSerial && matchesConfigurationPcbaSerialFormat(normalized)) {
      return { type: "FIELD", fieldName: "pcbaSerial" };
    }
  }

  for (const fieldName of SCAN_PRIORITY_FIELDS) {
    const config = fieldConfig[fieldName];
    if (!config) continue;
    if (fieldName === "pcbaSerial") {
      if (matchesConfigurationPcbaSerialFormat(normalized)) {
        return { type: "FIELD", fieldName };
      }
      continue;
    }
    const regexText = config?.validation?.regex;
    if (!regexText) continue;
    try {
      if (new RegExp(regexText).test(normalized)) {
        return { type: "FIELD", fieldName };
      }
    } catch {
      // Ignore invalid regex in config.
    }
  }

  return { type: "UNKNOWN" };
}

/** Form value wins, then post-configure device, then pre-configure device (Manufacturing-Dashboard configure.js). */
export function resolveConfigureSubmitField(
  formValue: string | null | undefined,
  afterDevice: SdTestDevice | null | undefined,
  beforeDevice: SdTestDevice | null | undefined,
  keys: readonly string[],
): string {
  const fromForm =
    formValue !== undefined && formValue !== null && String(formValue).trim() !== ""
      ? String(formValue).trim()
      : "";
  if (fromForm !== "") return fromForm;
  if (isDeviceLabelSerialKeyList(keys)) {
    const after = readDeviceLabelSerial(afterDevice);
    if (after !== "") return after;
    return readDeviceLabelSerial(beforeDevice);
  }
  const after = readDeviceField(afterDevice, keys);
  if (after !== "") return after;
  return readDeviceField(beforeDevice, keys);
}

export function readConfigurationPrefillValue(fieldName: string, device: SdTestDevice | null | undefined): string {
  if (!device) return "";
  switch (fieldName) {
    case "pcbaSerial":
      return readDeviceField(device, SDTEST_FIELD_KEYS.pcbaSerial);
    case "clampSerial":
      return readDeviceField(device, SDTEST_FIELD_KEYS.clampSerial);
    case "deviceLabelSerial":
      return readDeviceLabelSerial(device);
    case "iccid":
      return readDeviceField(device, SDTEST_FIELD_KEYS.iccid);
    default:
      return "";
  }
}

export function readPolledDeviceFirmware(device: SdTestDevice | null | undefined): string {
  if (!device) return "";
  const raw = device.firmwareVersion ?? device.firmware;
  return raw === undefined || raw === null ? "" : String(raw).trim();
}

export function readPolledDeviceModelNumber(device: SdTestDevice | null | undefined): string {
  if (!device) return "";
  const raw = device.modelNumber;
  return raw === undefined || raw === null ? "" : String(raw).trim();
}

export function readPolledDeviceMcuUuid(device: SdTestDevice | null | undefined): string {
  return readDeviceField(device, SDTEST_FIELD_KEYS.mcuUuid);
}

export function readPolledDeviceImei(device: SdTestDevice | null | undefined): string {
  return readDeviceField(device, SDTEST_FIELD_KEYS.imei);
}

/** True when SDTestApp lists a device but polled firmware or model is empty (likely stale session). */
export function isPolledDeviceDemographicsStale(device: SdTestDevice | null | undefined): boolean {
  if (!device) return false;
  if (readPolledDeviceFirmware(device) === "") return true;
  if (readPolledDeviceModelNumber(device) === "") return true;
  return false;
}

export function configureErrorSuggestsConnectorRefresh(message: string): boolean {
  return /not connected|device refresh|bootstrap|stale session|probe failed/i.test(message);
}

export function normalizeConfigurationMcuUuid(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-F]/g, "");
}

export function configurationMcuUuidsMatch(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const normalizedLeft = normalizeConfigurationMcuUuid(left);
  const normalizedRight = normalizeConfigurationMcuUuid(right);
  if (normalizedLeft === "" || normalizedRight === "") return false;
  return normalizedLeft === normalizedRight;
}

/** SDTestApp connector id for POST /devices/refresh (cable serial or connector id). */
export function readDeviceConnectorId(device: SdTestDevice | null | undefined): string {
  return String(device?.connectorId ?? device?.cableSerialNumber ?? device?.serialNumber ?? "").trim();
}
